import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { ApiClient } from '@/api/_legacy/generated-api'
import { Currency } from '@/utilities/currency'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { SavedState } from '../../../../tradebuilder-v2/types'
import { Errors, round } from '../../../../tradebuilder-v2/util'
import { TopUpWithdrawState } from '../../../types/TopUpWithdrawState'

const computeNewBalanceAndSaveState = (
  topUpWithrawState: TopUpWithdrawState,
  currentAmount: number,
  enteredAmount: number,
  minTopupAmount: number,
  minWithdrawAmount: number,
  minNoteSize: number,
  totalNoteValue: number
) => {
  const response = {
    newBalance: 0,
    canSave: false,
    warningBanner: undefined,
    closeThisTrade: false,
    closeAllTradesInNote: false
  }
  const changeAmount = Number.isNaN(enteredAmount) ? 0 : enteredAmount
  response.newBalance =
    topUpWithrawState === TopUpWithdrawState.Topup
      ? currentAmount + changeAmount
      : currentAmount - changeAmount

  if (topUpWithrawState !== TopUpWithdrawState.UnDef) {
    const minChangeAmount =
      topUpWithrawState === TopUpWithdrawState.Topup ? minTopupAmount : minWithdrawAmount
    response.canSave = changeAmount >= minChangeAmount
  } else {
    response.canSave = true
  }

  if (topUpWithrawState === TopUpWithdrawState.Withdraw) {
    if (changeAmount > round(currentAmount, 2)) {
      response.warningBanner = {
        title: 'There are insufficient funds to withdraw the amount requested',
        description: 'Please enter an amount lower than the current balance.'
      }
      response.canSave = false
    } else if (totalNoteValue - changeAmount < minNoteSize) {
      response.warningBanner = {
        title: `Withdrawing the requested amount would result in the Note going below its minimum size of R${minNoteSize}`,
        description:
          'Proceeding with this withdrawal will result in all trades belonging to this note being closed.'
      }
      response.closeAllTradesInNote = true
    } else {
      const overAllowedPercentage = changeAmount / currentAmount >= 0.95

      if (overAllowedPercentage) {
        response.warningBanner = {
          title: 'Warning, you are about to withdraw more than 95% of all funds',
          description: 'Withdrawing over 95% of all funds will result in the trade closing.'
        }
        response.closeThisTrade = true
      }
    }
  }
  return response
}

export type TopupWithdrawStepState = {
  canSave: boolean
  changeAmount: number
  closeAllTradesInNote: boolean
  closeThisTrade: boolean
  currency: Currency
  currentAmount: number
  errors: Errors
  minNoteSize: number
  minTopupAmount: number
  minWithdrawAmount: number
  newBalance: number
  success?: boolean
  topUpWithrawState: TopUpWithdrawState
  totalNoteValue: number
  warningBanner?: { description: string; title: string }
}

const initialState: TopupWithdrawStepState = {
  errors: {},
  topUpWithrawState: TopUpWithdrawState.UnDef,
  currentAmount: 0,
  currency: 'ZAR',
  changeAmount: 0,
  newBalance: 0,
  minTopupAmount: 0,
  minWithdrawAmount: 0,
  minNoteSize: 0,
  totalNoteValue: 0,
  closeThisTrade: false,
  closeAllTradesInNote: false,
  canSave: true
}

export type TopupWithdrawInitialise = {
  changeAmount: number
  client: ApiClient
  currentAmount: number
  topUpWithrawState: TopUpWithdrawState
  totalNoteValue: number
}

export type ChangeAmount = {
  updatedAmount: number
}

export const topupWithdrawSlice = createSlice({
  name: 'topupWithdrawStep',
  initialState,
  reducers: {
    reset: state => {
      state.canSave = true
      state.changeAmount = 0
      state.currentAmount = 0
      state.newBalance = 0
      state.errors = {}
    },
    init: (state, action: PayloadAction<TopupWithdrawInitialise>) => {
      state.errors = {}
      state.success = false
      state.currentAmount = action.payload.currentAmount
      state.changeAmount = action.payload.changeAmount
      state.topUpWithrawState = action.payload.topUpWithrawState
      state.totalNoteValue = action.payload.totalNoteValue
      const { client } = action.payload
      if (client && client.tradeBuildingSettings) {
        state.minTopupAmount = client.tradeBuildingSettings.minimumTopUpSize
        state.minWithdrawAmount = client.tradeBuildingSettings.minimumWithdrawalSize
        state.minNoteSize = client.tradeBuildingSettings.minimumNoteSize
      }

      const computedResponse = computeNewBalanceAndSaveState(
        state.topUpWithrawState,
        state.currentAmount,
        state.changeAmount,
        state.minTopupAmount,
        state.minWithdrawAmount,
        state.minNoteSize,
        state.totalNoteValue
      )
      state.newBalance = computedResponse.newBalance
      state.canSave = computedResponse.canSave
      state.warningBanner = computedResponse.warningBanner
      state.closeAllTradesInNote = computedResponse.closeAllTradesInNote
      state.closeThisTrade = computedResponse.closeThisTrade
    },
    updateSelectionState: (state, action: PayloadAction<TopUpWithdrawState>) => {
      state.topUpWithrawState = action.payload
      const computedResponse = computeNewBalanceAndSaveState(
        state.topUpWithrawState,
        state.currentAmount,
        state.changeAmount,
        state.minTopupAmount,
        state.minWithdrawAmount,
        state.minNoteSize,
        state.totalNoteValue
      )
      state.newBalance = computedResponse.newBalance
      state.canSave = computedResponse.canSave
      state.warningBanner = computedResponse.warningBanner
      state.closeAllTradesInNote = computedResponse.closeAllTradesInNote
      state.closeThisTrade = computedResponse.closeThisTrade
    },
    updateChangeAmount: (state, action: PayloadAction<ChangeAmount>) => {
      state.changeAmount = action.payload.updatedAmount
      const computedResponse = computeNewBalanceAndSaveState(
        state.topUpWithrawState,
        state.currentAmount,
        state.changeAmount,
        state.minTopupAmount,
        state.minWithdrawAmount,
        state.minNoteSize,
        state.totalNoteValue
      )
      state.newBalance = computedResponse.newBalance
      state.canSave = computedResponse.canSave
      state.warningBanner = computedResponse.warningBanner
      state.closeAllTradesInNote = computedResponse.closeAllTradesInNote
      state.closeThisTrade = computedResponse.closeThisTrade
    },
    updateAndMoveNext: () => {
      // handled by builder saga. dummy comment to keep linter happy
    },
    saveTradeTopupWithdrawSuccess: state => {
      state.errors = {}
      state.success = true
    },
    saveTradeTopupWithdrawFailure: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

const loadState = (savedState: SavedState) => async dispatch => {
  const pendingState = savedState?.account?.pendingAccountState
  let topupOrWithdrawState = TopUpWithdrawState.UnDef
  let changeAmount = 0
  if (pendingState && pendingState.deposit) {
    topupOrWithdrawState = TopUpWithdrawState.Topup
    changeAmount = pendingState.deposit
  } else if (pendingState && pendingState.withdrawal) {
    topupOrWithdrawState = TopUpWithdrawState.Withdraw
    changeAmount = pendingState.withdrawal
  }
  const associatedTrades = savedState?.note?.associatedTrades
  const totalNoteValue =
    associatedTrades.map(x => x.accountValue).reduce((a, b) => a + b, 0) +
    (savedState?.currentAccountValue || 0)

  dispatch(
    topupWithdrawSlice.actions.init({
      currentAmount: savedState?.currentAccountValue,
      changeAmount,
      topUpWithrawState: topupOrWithdrawState,
      client: savedState.client,
      totalNoteValue
    })
  )
}

export type TradeTopupWithdrawSave = {
  accountId: string
  changeAmount: number
  topUpWithrawState: TopUpWithdrawState
}

export const actions = { ...topupWithdrawSlice.actions, loadState }

export type TradeEditTopupWithdrawActions = typeof actions

export default topupWithdrawSlice.reducer
